import type { BadgeTone } from '@/components/ui';
import type { DispensingRx } from '@/features/admin-v2/api';
import type {
  DeliverySettings,
  DeliveryStatus,
  DeskStatus,
  StaffDelivery,
} from '@/features/deliveries/types';
import { CLINIC_TZ } from '@/lib/clinic-time';

const TZ = CLINIC_TZ;

export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  requested: 'Requested',
  packed: 'Packed',
  out_for_delivery: 'With the courier',
  delivered: 'Delivered',
  failed: 'Delivery failed',
  returned: 'Returned to clinic',
  cancelled: 'Cancelled',
};

export const deliveryStatusLabel = (s: string) =>
  DELIVERY_STATUS_LABEL[s as DeliveryStatus] ?? s.replaceAll('_', ' ');

export function deliveryTone(s: string): BadgeTone {
  if (s === 'delivered') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'packed' || s === 'out_for_delivery') return 'info';
  if (s === 'requested') return 'warning';
  return 'neutral';
}

/** Statuses still in the dispensary's hands (the API's `open`). */
const OPEN: DeliveryStatus[] = [
  'requested',
  'packed',
  'out_for_delivery',
  'failed',
];
export const isOpenDelivery = (s: string) => OPEN.includes(s as DeliveryStatus);

/** Open and past its deliver-by time. */
export const isLate = (d: StaffDelivery, now: Date) =>
  isOpenDelivery(d.status) && new Date(d.deliverBy).getTime() < now.getTime();

/** "RM 8.00". */
export const ringgit = (sen: number) => `RM ${(sen / 100).toFixed(2)}`;

/** "18:00" in clinic time. */
export function klClock(iso: string) {
  return new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const klDate = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);

/** "Today 18:00" or "Thu 23 Oct, 18:00" in clinic time. */
export function klDayClock(iso: string, now: Date) {
  const at = new Date(iso);
  if (klDate(at) === klDate(now)) return `Today ${klClock(iso)}`;
  return at.toLocaleString('en-GB', {
    timeZone: TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Minutes after midnight now, clinic time. */
export function klMinutesNow(now: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const n = (t: string) =>
    Number(parts.find((p) => p.type === t)?.value ?? '0') % 24;
  return n('hour') * 60 + Number(parts.find((p) => p.type === 'minute')?.value);
}

/** 900 → "15:00". */
export const hhmm = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** The `slot` sent when packing ("Today by 18:00"), from the request's deliver-by. */
export function packingSlot(d: StaffDelivery, now: Date) {
  const when = klDayClock(d.deliverBy, now);
  return when.startsWith('Today ')
    ? `Today by ${klClock(d.deliverBy)}`
    : `By ${when}`;
}

/** Same-day delivery at this branch today: off, before or after the cut-off. */
export function sameDayState(
  s: DeliverySettings | undefined,
  branchId: string,
  now: Date,
) {
  if (!s) return null;
  const offered = s.feeSen !== null && s.branchIds.includes(branchId);
  const minutes = klMinutesNow(now);
  return {
    offered,
    cutoff: hhmm(s.cutoffMin),
    deliverBy: hhmm(s.deliverByMin),
    cutoffPassed: minutes >= s.cutoffMin,
    deliverByPassed: minutes >= s.deliverByMin,
  };
}

/** Why an item is counter only (the API's `refusalReason`, same order). */
export type RefusalReason =
  'controlled' | 'psychotropic' | 'fridge' | 'not_in_catalogue';

export const REFUSAL_LABEL: Record<RefusalReason, string> = {
  controlled: 'Controlled: counter only, MyKad check',
  psychotropic: 'Psychotropic: counter only',
  fridge: 'Kept in the fridge: counter only',
  not_in_catalogue: 'Not in the medicine list: counter only',
};

export function refusalReason(
  item: DispensingRx['items'][number],
): RefusalReason | null {
  const c = item.catalogue;
  if (!c) return 'not_in_catalogue';
  if (c.controlled) return 'controlled';
  if (c.psychotropic) return 'psychotropic';
  if (c.fridge) return 'fridge';
  return null;
}

/** Items of a prescription that cannot be delivered, with the reason. */
export function refusedItems(rx: DispensingRx | null | undefined) {
  if (!rx) return [];
  return rx.items.flatMap((i) => {
    const reason = refusalReason(i);
    return reason ? [{ itemId: i.id, medicine: i.medicine, reason }] : [];
  });
}

/** Allowed courier steps from each status (the API's `NEXT_STATUS`). */
export const NEXT_STEPS: Record<string, DeskStatus[]> = {
  requested: ['cancelled'],
  packed: ['out_for_delivery', 'returned'],
  out_for_delivery: ['delivered', 'failed'],
  failed: ['out_for_delivery', 'returned'],
};
